import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, Trash2, AlertTriangle, Cloud, RefreshCw } from "lucide-react";
import { formatNumber, formatDate } from "@/lib/format";

export default function Imports() {
  const [behaviour, setBehaviour] = useState("update");
  const [uploading, setUploading] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [ccvSyncing, setCcvSyncing] = useState(false);
  const [ccvStatus, setCcvStatus] = useState(null);
  const [ccvDays, setCcvDays] = useState("7");

  const loadHistory = () => {
    api.get("/imports").then(({ data }) => setHistory(data || []));
  };

  const loadCcvStatus = () => {
    api.get("/ccv/status").then(({ data }) => setCcvStatus(data)).catch(() => {});
  };

  useEffect(() => { loadHistory(); loadCcvStatus(); }, []);

  const handleCcvSync = async () => {
    setCcvSyncing(true);
    setLastBatch(null);
    try {
      // Kick off background job (returns immediately with job_id)
      await api.post("/ccv/sync", null, {
        params: { days_back: parseInt(ccvDays, 10), behaviour },
      });
      toast.success("CCV sync gestart op de achtergrond");

      // Poll status every 3s until job_status != 'running'
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/ccv/status");
          setCcvStatus(data);
          if (data.job_status === "done") {
            clearInterval(poll);
            setCcvSyncing(false);
            if (data.last_result) setLastBatch(data.last_result);
            toast.success(
              `Sync klaar: ${data.last_result?.ccv_orders_fetched ?? 0} opgehaald · ${data.last_result?.nieuwe_orders ?? 0} nieuw`
            );
            loadHistory();
          } else if (data.job_status === "error") {
            clearInterval(poll);
            setCcvSyncing(false);
            toast.error(`CCV sync fout: ${data.job_message || "onbekende fout"}`);
          }
        } catch (err) {
          // network blip during polling — keep retrying
          console.warn("status poll failed", err);
        }
      }, 3000);
    } catch (e) {
      setCcvSyncing(false);
      toast.error(`Sync niet gestart: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setLastBatch(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("behaviour", behaviour);
    try {
      const { data } = await api.post("/imports/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastBatch(data);
      toast.success(`Import voltooid: ${data.nieuwe_orders} nieuwe, ${data.gewijzigde_orders} gewijzigd`);
      loadHistory();
    } catch (e) {
      toast.error(`Importfout: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleClearAll = async () => {
    if (!window.confirm("Weet je zeker dat je ALLE orders en orderregels wilt verwijderen?")) return;
    try {
      const { data } = await api.post("/imports/clear-all");
      toast.success(`Verwijderd: ${data.orders} orders, ${data.orderregels} regels`);
      loadHistory();
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-8" data-testid="imports-page">
      <header>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Data import</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Imports</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Synchroniseer rechtstreeks met CCV Shop, of upload een CSV-export. Dubbele orders worden automatisch herkend.
        </p>
      </header>

      <Card className="bg-white border-slate-200 p-5 sm:p-6 rounded-lg" data-testid="ccv-sync-card">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
              <Cloud className="h-5 w-5 text-sky-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">CCV Shop sync</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Automatisch elke nacht om 03:00 · of klik hieronder voor een directe sync.
              </p>
              {ccvStatus?.last_synced_at && (
                <p className="text-xs text-slate-400 mt-1" data-testid="ccv-last-sync">
                  Laatste sync: {formatDate(ccvStatus.last_synced_at)}
                  {ccvStatus.last_result?.ccv_orders_fetched != null &&
                    ` · ${ccvStatus.last_result.ccv_orders_fetched} orders opgehaald`}
                </p>
              )}
              {ccvStatus?.job_status === "running" && ccvStatus?.job_message && (
                <p className="text-xs text-sky-700 mt-1 flex items-center gap-1.5" data-testid="ccv-progress">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse" />
                  {ccvStatus.job_message}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Sync periode</label>
            <Select value={ccvDays} onValueChange={setCcvDays}>
              <SelectTrigger className="w-40 h-9" data-testid="ccv-days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">Laatste 2 dagen</SelectItem>
                <SelectItem value="7">Laatste 7 dagen</SelectItem>
                <SelectItem value="30">Laatste 30 dagen</SelectItem>
                <SelectItem value="90">Laatste 90 dagen</SelectItem>
                <SelectItem value="365">Laatste jaar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleCcvSync}
            disabled={ccvSyncing}
            className="bg-sky-700 hover:bg-sky-800 text-white h-9"
            data-testid="ccv-sync-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${ccvSyncing ? "animate-spin" : ""}`} />
            {ccvSyncing ? "Bezig met syncen…" : "Sync nu van CCV"}
          </Button>
          <p className="text-[11px] text-slate-400 max-w-md">
            Sync kan een paar minuten duren bij grotere periodes (rate-limit 150 req/min).
          </p>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 p-5 sm:p-6 rounded-lg">
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <label className="text-sm font-medium text-slate-700">Importgedrag</label>
          <Select value={behaviour} onValueChange={setBehaviour}>
            <SelectTrigger className="w-64" data-testid="import-behaviour-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="update">Bestaande orders bijwerken bij wijziging</SelectItem>
              <SelectItem value="skip">Bestaande orders overslaan</SelectItem>
              <SelectItem value="markonly">Markeer gewijzigd, niet overschrijven</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              data-testid="clear-data-btn"
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Wis alle orders
            </Button>
          </div>
        </div>

        <label
          htmlFor="csv-file-input"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-lg p-8 sm:p-12 text-center transition-all cursor-pointer flex flex-col items-center gap-3 ${
            dragOver
              ? "border-sky-500 bg-sky-50"
              : "border-slate-300 bg-slate-50/50 hover:border-sky-400 hover:bg-slate-50"
          }`}
          data-testid="csv-upload-dropzone"
        >
          <Upload className="h-10 w-10 text-slate-400" />
          <p className="text-base font-medium text-slate-900">
            Sleep een CSV-bestand of klik om te kiezen
          </p>
          <p className="text-xs text-slate-500">
            Ondersteuning voor tab-gescheiden CCV Shop exports (.csv)
          </p>
          <input
            id="csv-file-input"
            data-testid="csv-file-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>

        {uploading && (
          <div className="mt-6 flex items-center gap-3 text-sm text-slate-600">
            <div className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
            CSV wordt verwerkt…
          </div>
        )}
      </Card>

      {lastBatch && (
        <Card className="bg-white border-emerald-200 p-6 rounded-lg" data-testid="import-result-card">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-slate-900">Importverslag</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <Stat label="Regels gelezen" value={lastBatch.aantal_regels} />
            <Stat label="Unieke orders" value={lastBatch.unieke_orders} />
            <Stat label="Nieuwe orders" value={lastBatch.nieuwe_orders} accent="emerald" />
            <Stat label="Overgeslagen" value={lastBatch.bestaande_orders_overgeslagen} />
            <Stat label="Gewijzigd" value={lastBatch.gewijzigde_orders} accent="amber" />
            <Stat label="Uitgesloten" value={lastBatch.uitgesloten_orders} />
            <Stat label="Controle nodig" value={lastBatch.controle_nodig} accent={lastBatch.controle_nodig > 0 ? "amber" : "slate"} />
          </div>
          {lastBatch.gewijzigde_lijst?.length > 0 && (
            <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Gewijzigde orders ({lastBatch.gewijzigde_lijst.length})
              </p>
              <p className="text-xs text-amber-900/80 break-words">
                {lastBatch.gewijzigde_lijst.slice(0, 50).join(", ")}
                {lastBatch.gewijzigde_lijst.length > 50 && " …"}
              </p>
            </div>
          )}
        </Card>
      )}

      <Card className="bg-white border-slate-200 p-6 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-900">Importgeschiedenis</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="import-history-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-2.5 px-3 font-semibold">Bestand</th>
                <th className="text-left py-2.5 px-3 font-semibold">Datum</th>
                <th className="text-right py-2.5 px-3 font-semibold">Regels</th>
                <th className="text-right py-2.5 px-3 font-semibold">Nieuw</th>
                <th className="text-right py-2.5 px-3 font-semibold">Gewijzigd</th>
                <th className="text-right py-2.5 px-3 font-semibold">Overgeslagen</th>
                <th className="text-right py-2.5 px-3 font-semibold">Uitgesloten</th>
                <th className="text-right py-2.5 px-3 font-semibold">Controle</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan="8" className="text-center text-slate-400 py-8">Nog geen imports.</td></tr>
              )}
              {history.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-2.5 px-3 text-slate-900 max-w-xs truncate">{b.bestandsnaam}</td>
                  <td className="py-2.5 px-3 text-slate-600">{formatDate(b.importdatum)}</td>
                  <td className="py-2.5 px-3 text-right text-slate-900">{formatNumber(b.aantal_regels)}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-700 font-medium">{formatNumber(b.nieuwe_orders)}</td>
                  <td className="py-2.5 px-3 text-right text-amber-700">{formatNumber(b.gewijzigde_orders)}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{formatNumber(b.bestaande_orders_overgeslagen)}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{formatNumber(b.uitgesloten_orders)}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{formatNumber(b.controle_nodig)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const colors = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold tracking-tight ${colors[accent]}`}>{formatNumber(value)}</p>
    </div>
  );
}
